import React, { useEffect, useRef } from 'react';
import { useMediaQuery } from 'usehooks-ts';
import { Container, Row, Col, Input, Label } from 'reactstrap';
import JuryProjectCard from './JuryProjectCard/JuryProjectCard';
import JuryProjectDetails from './JuryProjectDetails/JuryProjectDetails';
import JuryCategoryFilter from './JuryCategoryFilter';
import styles from './JuryProjectsList.module.scss';
import { useJuryStore, useJuryPointsConfiguration, useJuryVotingType } from '@core/frontend/stores';
import { useJuryFilters } from './useJuryFilters';
import { useJuryCopy } from '../juryCopy.jsx';

/**
 * Shared grid UI for jury project views (Teilnehmer + Merkliste).
 * Uses useJuryFilters for URL-driven state management.
 */
function JuryProjectsGrid({ projects, showCount = true, emptyMessage, showUnratedFilter: allowUnratedFilter = true }) {
    const copy = useJuryCopy();
    const emptyText = emptyMessage ?? copy.emptyProjectListMessage;
    const isDesktop = useMediaQuery('(min-width: 992px)');
    const cardsRef = useRef(new Map());

    const votingType = useJuryVotingType();
    const pointsConfig = useJuryPointsConfiguration();
    const isPointsMode = votingType === 'points';
    const showUnratedFilter = allowUnratedFilter && isPointsMode && pointsConfig.mustRateAllProjects;

    const {
        selectedBadges,
        sort,
        q,
        page,
        project: selectedProjectId,
        unrated,
        filteredProjects,
        currentProjects,
        totalPages,
        patchParams,
        handlePageChange,
        handleCardSelect,
        resetFilters,
    } = useJuryFilters(projects, isDesktop);

    const loading = useJuryStore((s) => s.loading);
    const allProjects = useJuryStore((s) => s.projects);
    const points = useJuryStore((s) => s.points);
    const ratedCount = allProjects.filter((p) => points[p._id] !== undefined).length;

    useEffect(() => {
        if (isDesktop && !loading && selectedProjectId) {
            const timer = setTimeout(() => {
                const cardNode = cardsRef.current.get(selectedProjectId);
                if (cardNode) {
                    cardNode.scrollIntoView({ behavior: 'auto', block: 'nearest' });
                }
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isDesktop, loading, selectedProjectId]);

    const selectedProject =
        filteredProjects.find((p) => p._id === selectedProjectId) ??
        currentProjects.find((p) => p._id === selectedProjectId) ??
        null;

    return (
        <Container className="my-4">
            {showCount && (
                <Row id="jury-count-row" className="mb-3">
                    <Col>
                        <p className={styles.ProjectCount}>
                            {filteredProjects.length === projects.length ? (
                                `Wir freuen uns über ${projects.length} Teilnehmer!`
                            ) : (
                                <>
                                    <span>
                                        {filteredProjects.length} von {projects.length} {copy.entityLabel} werden mit
                                        den aktuellen Filtern angezeigt.
                                    </span>
                                    <button
                                        type="button"
                                        className={`btn btn-link ${styles.FilterResetButton}`}
                                        onClick={resetFilters}
                                    >
                                        Filter zurücksetzen
                                    </button>
                                </>
                            )}
                            {filteredProjects.length > currentProjects.length && (
                                <span className={styles.PageInfo}>
                                    {' '}
                                    (Seite&nbsp;{page}&nbsp;von&nbsp;{totalPages})
                                </span>
                            )}
                        </p>
                        {isPointsMode && (
                            <p className={styles.RatedCount}>
                                {ratedCount} von {allProjects.length} Teilnehmern bewertet
                            </p>
                        )}
                    </Col>
                </Row>
            )}

            <Row id="jury-filters-row" className="mb-3 gap-3 gap-md-0">
                <Col md={4}>
                    <Label className={styles.FilterLabel} for="category-filter" id="category-filter-label">
                        Kategorie
                    </Label>
                    <JuryCategoryFilter
                        selectedBadges={selectedBadges}
                        onChange={(badges) => patchParams({ badges, page: 1, project: null })}
                    />
                </Col>
                <Col md={4}>
                    <Label className={styles.FilterLabel} for="sort-by">
                        Sortieren
                    </Label>
                    <Input
                        id="sort-by"
                        type="select"
                        value={sort}
                        onChange={(e) => patchParams({ sort: e.target.value, page: 1, project: null })}
                    >
                        {copy.sortOptions.map(({ value, label }) => (
                            <option key={value} value={value}>
                                {label}
                            </option>
                        ))}
                    </Input>
                </Col>
                <Col md={4}>
                    <Label className={styles.FilterLabel} for="search-term">
                        Teilnehmer suchen
                    </Label>
                    <Input
                        id="search-term"
                        type="text"
                        placeholder={copy.searchPlaceholder}
                        value={q}
                        onChange={(e) => patchParams({ q: e.target.value, page: 1, project: null })}
                    />
                </Col>
            </Row>

            {showUnratedFilter && (
                <Row id="jury-unrated-filter-row" className="mb-3">
                    <Col>
                        <Input
                            id="unrated-filter"
                            type="checkbox"
                            checked={unrated}
                            onChange={(e) =>
                                patchParams({ unrated: e.target.checked ? '1' : undefined, page: 1, project: null })
                            }
                        />
                        <Label className={`${styles.FilterLabel} ms-2 mb-0 d-inline-block`} for="unrated-filter">
                            Nur unbewertete Teilnehmer anzeigen
                        </Label>
                    </Col>
                </Row>
            )}

            {loading ? (
                <Row>
                    <Col className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Lädt...</span>
                        </div>
                    </Col>
                </Row>
            ) : (
                <>
                    <Row id="jury-split-pane" className={styles.SplitPane}>
                        <Col xs={12} lg={4} xxl={3} className={styles.ListPane}>
                            <ul className={styles.ListColumn} aria-label="Teilnehmer Liste">
                                {currentProjects.map((project) => (
                                    <li key={project._id}>
                                        <JuryProjectCard
                                            ref={(node) => {
                                                if (node) {
                                                    cardsRef.current.set(project._id, node);
                                                } else {
                                                    cardsRef.current.delete(project._id);
                                                }
                                            }}
                                            id={`project-card-${project._id}`}
                                            project={project}
                                            isSelected={project._id === selectedProjectId}
                                            onSelect={handleCardSelect}
                                            showInlineDetails={!isDesktop}
                                        />
                                    </li>
                                ))}
                            </ul>
                        </Col>
                        {isDesktop && (
                            <Col lg={8} xxl={9} className={styles.DetailsPane}>
                                <div id="jury-details-pane" className={styles.DetailsSticky}>
                                    {selectedProject ? (
                                        <JuryProjectDetails project={selectedProject} />
                                    ) : (
                                        <p className="text-muted">
                                            Wählen Sie einen Teilnehmer aus, um die Projektdetails zu sehen.
                                        </p>
                                    )}
                                </div>
                            </Col>
                        )}
                    </Row>

                    {totalPages > 1 && (
                        <Row>
                            <Col>
                                <nav className={styles.Pagination} aria-label="Seitennavigation">
                                    <button
                                        className={styles.PageButton}
                                        onClick={() => handlePageChange(page - 1)}
                                        disabled={page === 1}
                                        aria-label="Vorherige Seite"
                                    >
                                        ← Zurück
                                    </button>
                                    <div className={styles.PageNumbers}>
                                        {[...Array(totalPages)].map((_, index) => {
                                            const pageNumber = index + 1;
                                            const isActive = page === pageNumber;
                                            if (
                                                pageNumber === 1 ||
                                                pageNumber === totalPages ||
                                                (pageNumber >= page - 2 && pageNumber <= page + 2)
                                            ) {
                                                return (
                                                    <button
                                                        key={pageNumber}
                                                        className={`${styles.PageNumber} ${isActive ? styles.PageNumberActive : ''}`}
                                                        onClick={() => handlePageChange(pageNumber)}
                                                        aria-current={isActive ? 'page' : undefined}
                                                        aria-label={`Seite ${pageNumber}`}
                                                    >
                                                        {pageNumber}
                                                    </button>
                                                );
                                            } else if (pageNumber === page - 3 || pageNumber === page + 3) {
                                                return (
                                                    <span
                                                        key={pageNumber}
                                                        className={styles.PageEllipsis}
                                                        aria-hidden="true"
                                                    >
                                                        ...
                                                    </span>
                                                );
                                            }
                                            return null;
                                        })}
                                    </div>
                                    <button
                                        className={styles.PageButton}
                                        onClick={() => handlePageChange(page + 1)}
                                        disabled={page === totalPages}
                                        aria-label="Nächste Seite"
                                    >
                                        Weiter →
                                    </button>
                                </nav>
                            </Col>
                        </Row>
                    )}

                    {filteredProjects.length === 0 && <p className="mt-3">{emptyText}</p>}
                </>
            )}
        </Container>
    );
}

export default JuryProjectsGrid;
